// src/features/chat/scheduledMessages.ts
// Scheduled chat messages: the REST call (no generated endpoint exists yet —
// hand-written against getApiClient like the generated ones do), the shared
// hook both composers use, and the date helpers for the schedule popover.
//
// Response parsing is defensive (same idiom as the entity panel): the
// ListMessagesResponse shape nests `{messages: {messages: [...]}}` but some
// bindings hand back a bare array.

import { useCallback, useEffect, useState } from 'react';
import { getApiClient } from '@core/api';
import { DeleteMessage } from '@core/api/endpoints/chat/delete_message';

export async function ListScheduledMessages(conversationId: string): Promise<any> {
  const client = getApiClient();
  const path = `/chat/conversations/${encodeURIComponent(conversationId)}/messages/scheduled`;
  return client.get<any>(path);
}

function scheduledMessageId(msg: any): string {
  return msg?.entity?.id ?? msg?.id ?? '';
}

export interface ScheduledMessagesApi {
  messages: any[];
  refresh: () => Promise<void>;
  /** Cancel = DeleteMessage; optimistic removal, rollback by refetch. */
  cancel: (messageId: string) => Promise<void>;
}

/**
 * Loads and tracks the scheduled messages of one conversation. `refreshKey`
 * is the callers' poke after they scheduled a new one (the composers own the
 * send, the hook owns the list).
 */
export function useScheduledMessages(
  conversationId: string | null | undefined,
  refreshKey = 0,
): ScheduledMessagesApi {
  const [messages, setMessages] = useState<any[]>([]);

  const refresh = useCallback(async () => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    try {
      const res = await ListScheduledMessages(conversationId);
      const raw = res?.messages;
      const items: any[] = Array.isArray(raw) ? raw : (raw?.messages ?? []);
      setMessages(items);
    } catch {
      // Backend without the endpoint yet (or transient failure) — the bar
      // just stays hidden.
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    void refresh();
  }, [refresh, refreshKey]);

  const cancel = useCallback(async (messageId: string) => {
    if (!conversationId || !messageId) return;
    setMessages((prev) => prev.filter((m) => scheduledMessageId(m) !== messageId));
    try {
      await DeleteMessage(conversationId, messageId);
    } catch {
      // Failed cancel — refetch so the row comes back if it still exists.
      void refresh();
    }
  }, [conversationId, refresh]);

  return { messages, refresh, cancel };
}

// --- Date helpers ---------------------------------------------------------------

/** `datetime-local` inputs want 'YYYY-MM-DDTHH:mm' in *local* time. */
export function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function presetInOneHour(): Date {
  return new Date(Date.now() + 60 * 60 * 1000);
}

export function presetTomorrowAtNine(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

/** Null when the input value is empty/unparseable. */
export function parseLocalInputValue(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatScheduledTime(iso: string | undefined, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}
